import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import UiIcon from './icons/UiIcon';
import { Badge } from './Rmc2Kit';
import useDebouncedValue from '../hooks/useDebouncedValue';
import useScopePermissions from '../hooks/useScopePermissions';
import { useMembers } from '../hooks/useRmc2Query';

/**
 * "Pull up that member" — the admin's single most frequent intent, in the
 * topbar instead of three navigations deep (Rule 56).
 *
 * Opens on click or ⌘/Ctrl-K, searches the server (so it finds members on page
 * 40, not just the ones already loaded), and lands on the Members tab filtered
 * to the chosen member. It renders only for principals who can see more than
 * their own row — a member searching the directory is exactly what the
 * branch-scope predicate exists to prevent, and the query would return nothing
 * useful anyway.
 */
export default function GlobalMemberSearch() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasAnyScope } = useScopePermissions();

  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState('');
  const debounced = useDebouncedValue(term, 300);

  const inputRef = useRef(null);
  const wrapRef = useRef(null);

  const canSearch = hasAnyScope(['members.view-all', 'members.manage', 'system:users-management']);

  const query = useMembers(
    { per_page: 8, q: debounced, sort: 'name', dir: 'asc' },
    { enabled: open && debounced.trim().length >= 2 },
  );

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen(true);
      }
      if (e.key === 'Escape') setOpen(false);
    };

    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (!open) return undefined;

    inputRef.current?.focus();
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  if (!canSearch) return null;

  const rows = query.data?.items ?? [];
  const searching = debounced.trim().length >= 2;

  const pick = (member) => {
    setOpen(false);
    setTerm('');
    navigate(`/members?q=${encodeURIComponent(member.id)}`);
  };

  return (
    <div className="gsearch" ref={wrapRef}>
      <button
        type="button"
        className="shell-btn gsearch-trigger"
        aria-label={t('rmc2.search.label')}
        title={t('rmc2.search.hint')}
        onClick={() => setOpen((v) => !v)}
      >
        <UiIcon name="search" className="h-4 w-4" />
        <span className="gsearch-trigger-text">{t('rmc2.search.label')}</span>
      </button>

      {open && (
        <div className="gsearch-panel" role="dialog" aria-label={t('rmc2.search.label')}>
          <input
            ref={inputRef}
            type="search"
            className="rmc2-input"
            placeholder={t('rmc2.members.searchPlaceholder')}
            aria-label={t('rmc2.search.label')}
            value={term}
            onChange={(e) => setTerm(e.target.value)}
          />

          <div className="gsearch-results" role="listbox" aria-label={t('rmc2.search.results')}>
            {!searching && (
              <p className="gsearch-empty">{t('rmc2.search.typeMore')}</p>
            )}

            {searching && query.isLoading && (
              <p className="gsearch-empty" role="status">{t('app.loading')}</p>
            )}

            {searching && !query.isLoading && rows.length === 0 && (
              <p className="gsearch-empty">{t('rmc2.members.emptyFiltered')}</p>
            )}

            {rows.map((m) => (
              <button
                key={m.id}
                type="button"
                role="option"
                aria-selected="false"
                className="gsearch-row"
                onClick={() => pick(m)}
              >
                <span className="gsearch-row-main">
                  <span className="gsearch-row-name">{m.name}</span>
                  <span className="gsearch-row-meta">
                    {m.id}
                    {m.branch_name ? ` · ${m.branch_name}` : ''}
                  </span>
                </span>
                <Badge status={m.status} />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
